import { Spinner } from '@frontend/components/common';
import { DetailActionsFooter, ModalType, useDetailActionsOverContent } from '@frontend/components/mobile';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { TransactionState, TransactionStateName } from '@frontend/transaction/types';
import { LuChevronRight } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import ConfirmDeleteModal from '../../../components/confirm-delete-modal/confirm-delete-modal.component';
import DeceasedSummaryCard from '../../../components/deceased-summary-card/deceased-summary-card.component';
import PinCodeModal from '../../../components/pin-code-modal/pin-code-modal.component';
import TransactionHistoryModal from '../../../components/transaction-history-modal/transaction-history-modal.component';
import { NavigationHeader } from '../../../header/navigation-header/navigation-header.component';
import useTransactionDetail from './transaction-detail.controller';

const ID = 'nivaliz-transaction-detail';

const TransactionDetail = () => {
    const viewProps = useTransactionDetail();

    const showDeleteButton = viewProps.transaction?.state === TransactionStateName.TRANSACTION_CREATED;
    const showPinCodeButton =
        !!viewProps.pinCode &&
        (viewProps.transaction?.state === TransactionStateName.TRANSACTION_PICK_UP_PENDING ||
            viewProps.transaction?.state === TransactionStateName.TRANSACTION_DROP_OFF_PENDING);
    const showActions = !!(showDeleteButton || showPinCodeButton || viewProps.onContinue);
    const actionsFooter = useDetailActionsOverContent(showActions, viewProps.transaction?.id);

    if (viewProps.isLoading) {
        return (
            <div
                id={ID}
                className='flex h-dvh w-full flex-col font-medium'>
                <NavigationHeader
                    title={viewProps.typeLabel ?? CommonMessage.OBJECTS.TRANSACTION.DEFAULT}
                    onBack={() => viewProps.navigate(-1)}
                />
                <div className='flex h-full w-full items-center justify-center'>
                    <Spinner />
                </div>
            </div>
        );
    }

    if (!viewProps.transaction) {
        return null;
    }

    const buttons = [
        {
            label: CommonMessage.BUTTONS.DELETE,
            onClick: () => viewProps.setDeleteModal(true),
            hide: !showDeleteButton
        },
        {
            label: CommonMessage.BUTTONS.CONTINUE,
            onClick: () => viewProps.onContinue?.(),
            hide: !viewProps.onContinue
        },
        {
            label:
                viewProps.transaction.state === TransactionStateName.TRANSACTION_PICK_UP_PENDING ? (
                    <FormattedMessage
                        id='transaction-card.show-pickup-code'
                        defaultMessage='Show pick-up code'
                        description='Show pick-up code text'
                    />
                ) : (
                    <FormattedMessage
                        id='transaction-card.show-dropoff-code'
                        defaultMessage='Show drop-off code'
                        description='Show drop-off code text'
                    />
                ),
            onClick: () => viewProps.setShowPinCodeModal(true),
            hide: !showPinCodeButton
        }
    ];

    return (
        <div
            id={ID}
            className='relative flex h-dvh w-full flex-col font-medium'>
            <NavigationHeader
                title={viewProps.typeLabel ?? CommonMessage.OBJECTS.TRANSACTION.DEFAULT}
                onBack={() => viewProps.navigate(-1)}
            />

            <div
                ref={actionsFooter.scrollContainerRef}
                onScroll={showActions ? actionsFooter.onScrollBody : undefined}
                className={`no-scrollbar flex w-full flex-1 flex-col gap-3 overflow-y-scroll px-5 pt-6 ${showActions ? 'pb-30' : 'pb-20'}`}>
                <div className='flex w-full flex-col gap-6'>
                    <h2 className='text-foreground-default text-lg font-semibold'>{viewProps.transactionTitle}</h2>

                    <DeceasedSummaryCard
                        accountId={viewProps.transaction.account_id}
                        deceasedId={viewProps.transaction.deceased?.entity_id ?? viewProps.transaction.deceased_id}
                    />

                    <div className='flex w-full flex-col gap-3'>
                        <p>{CommonMessage.DETAIL.DETAILS}</p>

                        <div className='bg-background-surface flex w-full flex-col gap-2 rounded-2xl p-4'>
                            <div className='flex h-6 w-full items-center justify-between'>
                                <div className='text-brand-muted'>{CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}</div>
                                <div
                                    className={`${TransactionState.getType(viewProps.transaction.state)} flex w-fit items-center justify-center rounded-full px-2 py-1`}>
                                    <p className='text-background-surface text-center text-sm font-semibold text-nowrap'>
                                        {TransactionState.getByValue(viewProps.transaction.state)?.displayName}
                                    </p>
                                </div>
                            </div>

                            {viewProps.typeLabel && (
                                <div className='flex h-6 w-full items-center justify-between'>
                                    <p className='text-brand-muted'>{CommonMessage.OBJECTS.COMMON_FIELDS.TYPE}</p>
                                    <p>{viewProps.typeLabel}</p>
                                </div>
                            )}

                            {viewProps.transaction.tracking_number && (
                                <div className='flex h-6 w-full items-center justify-between'>
                                    <p className='text-brand-muted'>{CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER}</p>
                                    <p>{viewProps.transaction.tracking_number}</p>
                                </div>
                            )}

                            <div className='flex h-6 w-full items-center justify-between'>
                                <p className='text-brand-muted'>{CommonMessage.OBJECTS.SPOT.DISTRI_SPOT}</p>
                                <p>{viewProps.transaction.spot?.name ?? '/'}</p>
                            </div>

                            <div className='flex h-6 w-full items-center justify-between'>
                                <p className='text-brand-muted'>{CommonMessage.OBJECTS.SLOT.DEFAULT}</p>
                                <p>{viewProps.transaction.slot?.name ?? '/'}</p>
                            </div>
                        </div>
                    </div>

                    {viewProps.transaction.shipping_notes && (
                        <div className='flex w-full flex-col gap-3'>
                            <p>{CommonMessage.OBJECTS.TRANSACTION.NOTES}</p>
                            <div className='bg-background-surface flex w-full rounded-2xl p-4'>
                                <p>{viewProps.transaction.shipping_notes}</p>
                            </div>
                        </div>
                    )}

                    {viewProps.transaction.senders && viewProps.transaction.senders.length > 0 && (
                        <div className='bg-background-surface inline-flex flex-col items-start justify-start gap-4 self-stretch rounded-2xl p-4'>
                            <div className='inline-flex items-center justify-start gap-4 self-stretch'>
                                <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                    <ICONS.ENTITY.USER className='size-1/2' />
                                </div>
                                <div className='text-foreground-default justify-start text-base font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.SENDER}</div>
                            </div>
                            <div className='flex w-full flex-col gap-2'>
                                {viewProps.transaction.senders.map((sender) => (
                                    <p
                                        key={sender.entity_id}
                                        className='text-brand-muted justify-start text-base font-medium'>
                                        {sender.name ?? '/'}
                                    </p>
                                ))}
                            </div>
                        </div>
                    )}

                    {viewProps.transaction.receivers && viewProps.transaction.receivers.length > 0 && (
                        <div className='bg-background-surface inline-flex flex-col items-start justify-start gap-4 self-stretch rounded-2xl p-4'>
                            <div className='inline-flex items-center justify-start gap-4 self-stretch'>
                                <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                    <ICONS.ENTITY.USER className='size-1/2' />
                                </div>
                                <div className='text-foreground-default justify-start text-base font-semibold'>
                                    {CommonMessage.OBJECTS.TRANSACTION.RECEIVER}
                                </div>
                            </div>
                            <div className='flex w-full flex-col gap-2'>
                                {viewProps.transaction.receivers.map((receiver) => (
                                    <p
                                        key={receiver.entity_id}
                                        className='text-brand-muted justify-start text-base font-medium'>
                                        {receiver.name ?? '/'}
                                    </p>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        type='button'
                        onClick={() => viewProps.setShowHistoryModal(true)}
                        className='bg-background-surface flex w-full items-center justify-between rounded-2xl p-4 text-left'>
                        <span className='text-foreground-default text-base font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.HISTORY}</span>
                        <LuChevronRight className='text-brand-muted size-5 shrink-0' />
                    </button>
                </div>
            </div>

            {showActions && (
                <DetailActionsFooter
                    actionsOverContent={actionsFooter.actionsOverContent}
                    buttons={buttons}
                />
            )}

            {viewProps.deleteModal && (
                <ConfirmDeleteModal
                    handleClose={() => viewProps.setDeleteModal(false)}
                    show={viewProps.deleteModal}
                    type={ModalType.PROMPT}
                    onSubmit={viewProps.onDelete}
                />
            )}

            {viewProps.showPinCodeModal && viewProps.pinCode && (
                <PinCodeModal
                    show={viewProps.showPinCodeModal}
                    handleClose={() => viewProps.setShowPinCodeModal(false)}
                    pinCode={viewProps.pinCode}
                />
            )}

            {viewProps.showHistoryModal && viewProps.transaction && (
                <TransactionHistoryModal
                    show={viewProps.showHistoryModal}
                    handleClose={() => viewProps.setShowHistoryModal(false)}
                    transactionId={viewProps.transaction.id}
                />
            )}
        </div>
    );
};

export default TransactionDetail;
